import {
    ShareBaseFolder,
    ShareBaseLibrary,
    pxItemTypeGuards,
    UploadException
} from 'phoenix/domain';
import { UploadService } from '../../phoenix/services/uploadService';
import { IFolderService } from './folderService';

export type IFolderUploadService = FolderUploadService;
export interface UploadResult {
    cancelled: boolean;
    errors?: string[]
};

export class FolderUploadService {
    public static readonly $inject: string[] = [
        '$q',
        'gettextCatalog',
        'pxUploadService',
        'sbToastService',
        'sbFolderService',
        'sbLibraryService'
    ];

    private cancelled = false;
    private readonly documentLibraryErrorMessage: string;
    private documentLibraryErrorMessageQueued = false;

    constructor(
        private readonly $q: ng.IQService,
        private readonly gettextCatalog,
        private readonly pxUploadService: UploadService,
        private readonly sbToastService,
        private readonly sbFolderService: IFolderService,
        private readonly sbLibraryService
    ) {
        this.documentLibraryErrorMessage = this.gettextCatalog.getString('Documents cannot be added to libraries');
    }

    public uploadEntries(entries: any[], parent: ShareBaseFolder | ShareBaseLibrary): ng.IPromise<UploadResult> {
        this.cancelled = false;

        return this.$q.all<UploadResult>(entries.map((e) => this.uploadFolderHelper(e, parent)))
            .then((results) => {
                let result: UploadResult = {
                    cancelled: results.some((r) => r.cancelled)
                };

                let errors = results.reduce((err, result) => {
                    if (result.errors && result.errors.length > 0) {
                        err = err.concat(result.errors.filter(e => err.indexOf(e) < 0));
                    }
                    return err;
                }, [] as string[])
                    .filter(err => !!err);
                
                if (errors.length > 0) {
                    result.errors = errors;
                }

                return result
            });
    }

    public fileSystemIsSupported(dt: DataTransfer): boolean {
        return 'items' in dt && dt.items.length > 0 && 'webkitGetAsEntry' in dt.items[0];
    }

    public cancelAllCurrentUploads(): void {
        this.cancelled = true;
    }

    private uploadFolderHelper(fsEntry: Entry, parent: ShareBaseFolder | ShareBaseLibrary, path: string = ''): ng.IPromise<UploadResult> {
        return this.$q((resolve, reject) => {

            if (!this.cancelled) {
                if (fsEntry.isDirectory) {
                    // let folderEntry = fsEntry as DirectoryEntry;
                    let folderPromise: ng.IPromise<ShareBaseFolder>;
                    if (pxItemTypeGuards.isShareBaseFolder(parent)) {
                        folderPromise = this.sbFolderService.createFolder(fsEntry.name, parent);
                    } else {
                        folderPromise = this.sbLibraryService.createFolder(parent.LibraryId, fsEntry.name);
                    }

                    folderPromise.then((folder) => {
                        (fsEntry as DirectoryEntry).createReader()
                            .readEntries((entries) => {
                                let entryPromises = entries.map((entry) => entry.isDirectory ?
                                    this.uploadFolderHelper(entry, folder, this.joinPath(path, folder.FolderName)) :
                                    this.uploadFileEntry(entry as FileEntry, folder));
                                
                                this.$q.all<UploadResult>(entryPromises)
                                    .then((results) => {
                                        let result: UploadResult = {
                                            cancelled: results.some(r => r.cancelled)
                                        };

                                        let errors = results.reduce((err, curr) => err.concat(curr.errors), [])
                                            .filter(err => !!err);

                                        if (errors.length > 0) {
                                            result.errors = errors
                                        }

                                        resolve(result);                             
                                    });
                            });
                    })
                    .catch(() => {
                        resolve({
                            cancelled: false,
                            errors: [this.gettextCatalog.getString('Unable to upload {{folderName}}', { folderName: this.joinPath(path, fsEntry.name) })]
                        });
                    });
                } else {
                    if (pxItemTypeGuards.isShareBaseLibrary(parent)) {
                        resolve({
                            cancelled: false,
                            errors: [this.gettextCatalog.getString('Documents can\'t be added to the root of libraries.  Try creating or adding it to a folder.')]
                        });
                    } else {
                        this.uploadFileEntry(fsEntry as FileEntry, parent)
                            .then(resolve);
                    }
                }
            } else {
                resolve({ cancelled: true });
            }
        })
        
    }

    private uploadFileEntry(file: FileEntry, parent: ShareBaseFolder): ng.IPromise<UploadResult> {
        return this.$q((resolve, reject) => {
            file.file((f) => {
                if (!this.cancelled) {
                    this.pxUploadService.addFile(f, parent.FolderId, f.name, parent.Links.DocumentsUri)
                        .then(() => resolve({ cancelled: this.cancelled }))
                        .catch((e:UploadException) => {
                            resolve({
                                cancelled: this.cancelled, 
                                errors: [e.userMessage]
                            });
                        });
                }
                else {
                    resolve({ cancelled: this.cancelled });
                }
            });
        })
    }

    private joinPath(root: string, ...args: string[]) {
        let path = root.split('/')
            .concat(args)
            .join('/');
        
        if (path.charAt(0) === '/') {
            path = path.slice(1);
        }
        if (path.charAt(path.length - 1) === '/') {
            path = path.slice(-1);
        }

        return path;
    }
}


// WEBPACK FOOTER //
// ./src/sharebase/services/folderUploadService.ts